/**
 * Leviathan Web - Bill of Lading Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * BOL detail view with status actions and signature capture
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  Printer,
  Truck,
  Package,
  CheckCircle,
  XCircle,
  User,
  MapPin,
  Calendar,
  Fuel,
  PenTool,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface BOLItem {
  id: string;
  productId: string;
  productName: string;
  productCode: string | null;
  orderedQuantity: number;
  loadedQuantity: number;
  deliveredQuantity: number | null;
  unit: string;
  unitPrice: number;
  lineTotal: number;
  tankNumber: string | null;
  tankStartLevel: number | null;
  tankEndLevel: number | null;
  loadTemperature: number | null;
  deliveryTemperature: number | null;
  compartmentNumber: number | null;
}

interface BOL {
  id: string;
  bolNumber: string;
  status: string;
  orderId: string;
  dispatchId: string | null;
  customerId: string;
  customerName: string;
  customerAddress: string | null;
  customerCity: string | null;
  customerState: string | null;
  customerZip: string | null;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  deliveryInstructions: string | null;
  driverId: string | null;
  driverName: string | null;
  driverLicense: string | null;
  truckId: string | null;
  truckNumber: string | null;
  trailerNumber: string | null;
  loadingDate: string | null;
  loadingTime: string | null;
  loadedBy: string | null;
  sourceTerminal: string | null;
  sealNumber: string | null;
  deliveryDate: string | null;
  deliveryTime: string | null;
  receivedBy: string | null;
  receivedTitle: string | null;
  driverSignature: string | null;
  driverSignedAt: string | null;
  customerSignature: string | null;
  customerSignedAt: string | null;
  startMeterReading: number | null;
  endMeterReading: number | null;
  grossQuantity: number | null;
  netQuantity: number | null;
  temperature: number | null;
  totalQuantity: number;
  totalAmount: number;
  notes: string | null;
  internalNotes: string | null;
  deliveryNotes: string | null;
  createdAt: string;
  printedAt: string | null;
  items: BOLItem[];
  customer?: { name: string };
  driver?: { firstName: string; lastName: string };
  truck?: { unitNumber: string };
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: string }) {
  const defaultConfig = { color: 'text-text-secondary', bg: 'bg-panel-border/50', label: 'Draft' };
  const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
    DRAFT: defaultConfig,
    PENDING: { color: 'text-warning-amber', bg: 'bg-warning-amber/10', label: 'Pending' },
    IN_TRANSIT: { color: 'text-blue-400', bg: 'bg-blue-400/10', label: 'In Transit' },
    DELIVERED: { color: 'text-amber', bg: 'bg-amber/10', label: 'Delivered' },
    SIGNED: { color: 'text-success-green', bg: 'bg-success-green/10', label: 'Signed' },
    COMPLETED: { color: 'text-success-green', bg: 'bg-success-green/10', label: 'Completed' },
    VOID: { color: 'text-error-red', bg: 'bg-error-red/10', label: 'Void' },
  };

  const config = statusConfig[status] ?? defaultConfig;

  return (
    <span className={`px-3 py-1.5 text-xs uppercase tracking-wider font-mono ${config.color} ${config.bg}`}>
      {config.label}
    </span>
  );
}

/**
 * Info card component
 */
function InfoCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-panel-dark border border-panel-border relative">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="border-b border-panel-border px-4 py-2 bg-background/30">
        <h3 className="text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono">
          {title}
        </h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/**
 * BOL Detail Page Component
 */
const BOLDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [bol, setBol] = useState<BOL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureType, setSignatureType] = useState<'driver' | 'customer'>('customer');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (id) {
      fetchBOL();
    }
  }, [id]);

  const fetchBOL = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bol/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setBol(data);
      } else {
        navigate('/bol');
      }
    } catch (error) {
      console.error('Failed to fetch BOL:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (action: string, body: Record<string, unknown> = {}) => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/bol/${id}/${action}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        fetchBOL();
      }
    } catch (error) {
      console.error('Failed to update BOL:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVoid = async () => {
    const reason = prompt('Enter reason for voiding this BOL:');
    if (reason) {
      await updateStatus('void', { reason });
    }
  };

  // Signature canvas handlers
  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    const touch = 'touches' in e ? e.touches[0] : null;
    const x = touch ? touch.clientX - rect.left : (e as React.MouseEvent).clientX - rect.left;
    const y = touch ? touch.clientY - rect.top : (e as React.MouseEvent).clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const touch = 'touches' in e ? e.touches[0] : null;
    const x = touch ? touch.clientX - rect.left : (e as React.MouseEvent).clientX - rect.left;
    const y = touch ? touch.clientY - rect.top : (e as React.MouseEvent).clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    if (!canvasRef.current) return;
    const signature = canvasRef.current.toDataURL('image/png');

    const endpoint = signatureType === 'driver' ? 'driver-signature' : 'customer-signature';
    await updateStatus(endpoint, { signature });
    setShowSignatureModal(false);
  };

  const openSignatureModal = (type: 'driver' | 'customer') => {
    setSignatureType(type);
    setShowSignatureModal(true);
    // Initialize canvas after modal opens
    setTimeout(() => {
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.strokeStyle = '#FF9500';
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
        }
      }
    }, 100);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatQuantity = (qty: number) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(qty);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-tertiary font-mono">Loading BOL...</div>
      </div>
    );
  }

  if (!bol) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-tertiary font-mono">BOL not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/bol"
            className="p-2 text-text-tertiary hover:text-amber transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-amber font-mono">{bol.bolNumber}</h1>
              <StatusBadge status={bol.status} />
            </div>
            <p className="text-sm text-text-secondary font-mono mt-1">
              {bol.customerName}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to={`/bol/${id}/print`}
            className="flex items-center space-x-2 px-4 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber transition-colors"
          >
            <Printer className="w-4 h-4" />
            <span className="font-mono text-sm uppercase">Print</span>
          </Link>
        </div>
      </div>

      {/* Status Actions */}
      {bol.status !== 'VOID' && bol.status !== 'COMPLETED' && (
        <div className="bg-panel-dark border border-amber/30 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-text-secondary font-mono">
              <span className="text-amber">Status Actions:</span> Progress this BOL through the delivery workflow
            </div>
            <div className="flex items-center space-x-3">
              {bol.status === 'DRAFT' && (
                <button
                  onClick={() => updateStatus('loading', { loadingDate: new Date() })}
                  disabled={isProcessing}
                  className="flex items-center space-x-2 px-4 py-2 bg-warning-amber/20 text-warning-amber hover:bg-warning-amber/30 transition-colors font-mono text-sm uppercase disabled:opacity-50"
                >
                  <Package className="w-4 h-4" />
                  <span>Mark Loaded</span>
                </button>
              )}
              {bol.status === 'PENDING' && (
                <button
                  onClick={() => updateStatus('in-transit')}
                  disabled={isProcessing}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-400/20 text-blue-400 hover:bg-blue-400/30 transition-colors font-mono text-sm uppercase disabled:opacity-50"
                >
                  <Truck className="w-4 h-4" />
                  <span>Mark In Transit</span>
                </button>
              )}
              {bol.status === 'IN_TRANSIT' && (
                <button
                  onClick={() => updateStatus('delivery', { deliveryDate: new Date() })}
                  disabled={isProcessing}
                  className="flex items-center space-x-2 px-4 py-2 bg-amber/20 text-amber hover:bg-amber/30 transition-colors font-mono text-sm uppercase disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Record Delivery</span>
                </button>
              )}
              {(bol.status === 'DELIVERED' || bol.status === 'SIGNED') && !bol.customerSignature && (
                <button
                  onClick={() => openSignatureModal('customer')}
                  disabled={isProcessing}
                  className="flex items-center space-x-2 px-4 py-2 bg-success-green/20 text-success-green hover:bg-success-green/30 transition-colors font-mono text-sm uppercase disabled:opacity-50"
                >
                  <PenTool className="w-4 h-4" />
                  <span>Capture Signature</span>
                </button>
              )}
              {bol.status === 'SIGNED' && (
                <button
                  onClick={() => updateStatus('complete')}
                  disabled={isProcessing}
                  className="flex items-center space-x-2 px-4 py-2 bg-success-green text-background hover:bg-success-green/80 transition-colors font-mono text-sm uppercase disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Complete BOL</span>
                </button>
              )}
              <button
                onClick={handleVoid}
                disabled={isProcessing}
                className="flex items-center space-x-2 px-4 py-2 border border-error-red/50 text-error-red hover:bg-error-red/10 transition-colors font-mono text-sm uppercase disabled:opacity-50"
              >
                <XCircle className="w-4 h-4" />
                <span>Void</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer & Delivery Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoCard title="Customer Information">
              <div className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase text-text-tertiary font-mono mb-1">Name</div>
                  <div className="text-text-primary font-mono">{bol.customerName}</div>
                </div>
                {bol.customerAddress && (
                  <div>
                    <div className="text-[10px] uppercase text-text-tertiary font-mono mb-1">Billing Address</div>
                    <div className="text-text-secondary font-mono text-sm">
                      {bol.customerAddress}<br />
                      {bol.customerCity}, {bol.customerState} {bol.customerZip}
                    </div>
                  </div>
                )}
              </div>
            </InfoCard>

            <InfoCard title="Delivery Location">
              <div className="space-y-3">
                <div className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 text-amber mt-0.5" />
                  <div className="text-text-primary font-mono text-sm">
                    {bol.deliveryAddress}<br />
                    {bol.deliveryCity}, {bol.deliveryState} {bol.deliveryZip}
                  </div>
                </div>
                {bol.deliveryInstructions && (
                  <div>
                    <div className="text-[10px] uppercase text-text-tertiary font-mono mb-1">Instructions</div>
                    <div className="text-text-secondary font-mono text-sm">{bol.deliveryInstructions}</div>
                  </div>
                )}
              </div>
            </InfoCard>
          </div>

          {/* Line Items */}
          <InfoCard title="Products">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-panel-border">
                    <th className="text-left text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-2 py-2">Product</th>
                    <th className="text-right text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-2 py-2">Ordered</th>
                    <th className="text-right text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-2 py-2">Loaded</th>
                    <th className="text-right text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-2 py-2">Delivered</th>
                    <th className="text-right text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-2 py-2">Price</th>
                    <th className="text-right text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-2 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {bol.items.map((item) => (
                    <tr key={item.id} className="border-b border-panel-border/50">
                      <td className="px-2 py-3">
                        <div className="flex items-center space-x-2">
                          <Fuel className="w-4 h-4 text-amber" />
                          <div>
                            <div className="text-text-primary font-mono text-sm">{item.productName}</div>
                            {item.productCode && (
                              <div className="text-text-tertiary font-mono text-xs">{item.productCode}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-3 text-right">
                        <span className="font-mono text-sm text-text-secondary">
                          {formatQuantity(item.orderedQuantity)} {item.unit}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-right">
                        <span className="font-mono text-sm text-text-primary">
                          {formatQuantity(item.loadedQuantity)} {item.unit}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-right">
                        <span className="font-mono text-sm text-amber">
                          {item.deliveredQuantity !== null
                            ? `${formatQuantity(item.deliveredQuantity)} ${item.unit}`
                            : '-'}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-right">
                        <span className="font-mono text-sm text-text-secondary">
                          {formatCurrency(item.unitPrice)}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-right">
                        <span className="font-mono text-sm text-amber">
                          {formatCurrency(item.lineTotal)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-background/30">
                    <td colSpan={5} className="px-2 py-3 text-right">
                      <span className="font-mono text-sm text-text-tertiary uppercase">Total:</span>
                    </td>
                    <td className="px-2 py-3 text-right">
                      <span className="font-mono text-lg font-bold text-amber">
                        {formatCurrency(bol.totalAmount)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </InfoCard>

          {/* Notes */}
          {(bol.notes || bol.internalNotes || bol.deliveryNotes) && (
            <InfoCard title="Notes">
              <div className="space-y-4">
                {bol.notes && (
                  <div>
                    <div className="text-[10px] uppercase text-text-tertiary font-mono mb-1">Customer Notes</div>
                    <div className="text-text-secondary font-mono text-sm">{bol.notes}</div>
                  </div>
                )}
                {bol.internalNotes && (
                  <div>
                    <div className="text-[10px] uppercase text-warning-amber font-mono mb-1">Internal Notes</div>
                    <div className="text-text-secondary font-mono text-sm">{bol.internalNotes}</div>
                  </div>
                )}
                {bol.deliveryNotes && (
                  <div>
                    <div className="text-[10px] uppercase text-amber font-mono mb-1">Delivery Notes</div>
                    <div className="text-text-secondary font-mono text-sm">{bol.deliveryNotes}</div>
                  </div>
                )}
              </div>
            </InfoCard>
          )}
        </div>

        {/* Right Column - Driver/Truck & Signatures */}
        <div className="space-y-6">
          {/* Driver & Truck */}
          <InfoCard title="Driver & Truck">
            <div className="space-y-4">
              {bol.driverName && (
                <div className="flex items-start space-x-3">
                  <User className="w-5 h-5 text-amber mt-0.5" />
                  <div>
                    <div className="text-text-primary font-mono">{bol.driverName}</div>
                    {bol.driverLicense && (
                      <div className="text-text-tertiary font-mono text-xs">License: {bol.driverLicense}</div>
                    )}
                  </div>
                </div>
              )}
              {bol.truckNumber && (
                <div className="flex items-start space-x-3">
                  <Truck className="w-5 h-5 text-steel mt-0.5" />
                  <div>
                    <div className="text-text-primary font-mono">{bol.truckNumber}</div>
                    {bol.trailerNumber && (
                      <div className="text-text-tertiary font-mono text-xs">Trailer: {bol.trailerNumber}</div>
                    )}
                  </div>
                </div>
              )}
              {bol.sourceTerminal && (
                <div>
                  <div className="text-[10px] uppercase text-text-tertiary font-mono mb-1">Source Terminal</div>
                  <div className="text-text-secondary font-mono text-sm">{bol.sourceTerminal}</div>
                </div>
              )}
              {bol.sealNumber && (
                <div>
                  <div className="text-[10px] uppercase text-text-tertiary font-mono mb-1">Seal Number</div>
                  <div className="text-text-secondary font-mono text-sm">{bol.sealNumber}</div>
                </div>
              )}
            </div>
          </InfoCard>

          {/* Loading & Delivery Times */}
          <InfoCard title="Timeline">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Calendar className="w-4 h-4 text-text-tertiary" />
                <div>
                  <div className="text-[10px] uppercase text-text-tertiary font-mono">Created</div>
                  <div className="text-text-secondary font-mono text-sm">{formatDateTime(bol.createdAt)}</div>
                </div>
              </div>
              {bol.loadingDate && (
                <div className="flex items-center space-x-3">
                  <Package className="w-4 h-4 text-warning-amber" />
                  <div>
                    <div className="text-[10px] uppercase text-text-tertiary font-mono">Loaded</div>
                    <div className="text-text-secondary font-mono text-sm">
                      {formatDate(bol.loadingDate)} {bol.loadingTime || ''}
                    </div>
                  </div>
                </div>
              )}
              {bol.deliveryDate && (
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-amber" />
                  <div>
                    <div className="text-[10px] uppercase text-text-tertiary font-mono">Delivered</div>
                    <div className="text-text-secondary font-mono text-sm">
                      {formatDate(bol.deliveryDate)} {bol.deliveryTime || ''}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </InfoCard>

          {/* Signatures */}
          <InfoCard title="Signatures">
            <div className="space-y-4">
              {/* Driver Signature */}
              <div>
                <div className="text-[10px] uppercase text-text-tertiary font-mono mb-2">Driver Signature</div>
                {bol.driverSignature ? (
                  <div className="bg-background border border-panel-border p-2">
                    <img src={bol.driverSignature} alt="Driver Signature" className="max-h-16" />
                    <div className="text-text-tertiary font-mono text-xs mt-1">
                      Signed: {formatDateTime(bol.driverSignedAt)}
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => openSignatureModal('driver')}
                    className="w-full border border-dashed border-panel-border text-text-tertiary hover:text-amber hover:border-amber py-4 font-mono text-sm transition-colors"
                  >
                    <PenTool className="w-4 h-4 mx-auto mb-1" />
                    Click to sign
                  </button>
                )}
              </div>

              {/* Customer Signature */}
              <div>
                <div className="text-[10px] uppercase text-text-tertiary font-mono mb-2">Customer Signature</div>
                {bol.customerSignature ? (
                  <div className="bg-background border border-panel-border p-2">
                    <img src={bol.customerSignature} alt="Customer Signature" className="max-h-16" />
                    <div className="text-text-tertiary font-mono text-xs mt-1">
                      {bol.receivedBy && <span>{bol.receivedBy} - </span>}
                      Signed: {formatDateTime(bol.customerSignedAt)}
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => openSignatureModal('customer')}
                    className="w-full border border-dashed border-panel-border text-text-tertiary hover:text-amber hover:border-amber py-4 font-mono text-sm transition-colors"
                  >
                    <PenTool className="w-4 h-4 mx-auto mb-1" />
                    Click to sign
                  </button>
                )}
              </div>
            </div>
          </InfoCard>

          {/* Meter Readings */}
          {(bol.startMeterReading !== null || bol.endMeterReading !== null) && (
            <InfoCard title="Meter Readings">
              <div className="space-y-3">
                {bol.startMeterReading !== null && (
                  <div className="flex justify-between">
                    <span className="text-text-tertiary font-mono text-sm">Start:</span>
                    <span className="text-text-primary font-mono">{formatQuantity(bol.startMeterReading)}</span>
                  </div>
                )}
                {bol.endMeterReading !== null && (
                  <div className="flex justify-between">
                    <span className="text-text-tertiary font-mono text-sm">End:</span>
                    <span className="text-text-primary font-mono">{formatQuantity(bol.endMeterReading)}</span>
                  </div>
                )}
                {bol.netQuantity !== null && (
                  <div className="flex justify-between border-t border-panel-border pt-2">
                    <span className="text-text-tertiary font-mono text-sm">Net Delivered:</span>
                    <span className="text-amber font-mono font-bold">{formatQuantity(bol.netQuantity)} gal</span>
                  </div>
                )}
              </div>
            </InfoCard>
          )}
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <div className="bg-panel-dark border border-amber p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-mono text-amber uppercase">
                {signatureType === 'driver' ? 'Driver' : 'Customer'} Signature
              </h3>
              <button
                onClick={() => setShowSignatureModal(false)}
                className="text-text-tertiary hover:text-amber"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="border border-panel-border bg-background mb-4">
              <canvas
                ref={canvasRef}
                width={350}
                height={150}
                className="w-full cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>

            <div className="flex justify-between">
              <button
                onClick={clearSignature}
                className="px-4 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber font-mono text-sm uppercase transition-colors"
              >
                Clear
              </button>
              <div className="space-x-3">
                <button
                  onClick={() => setShowSignatureModal(false)}
                  className="px-4 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber font-mono text-sm uppercase transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveSignature}
                  className="px-4 py-2 bg-amber text-background hover:bg-amber/80 font-mono text-sm uppercase transition-colors"
                >
                  Save Signature
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BOLDetailPage;
